<template>
  <div
    v-if="totalItems > 0"
    class="pagination-container pagination-glass"
    :class="{ 'pagination-container--single-page': totalPages <= 1 }"
  >
    <div class="pagination-info" :key="`info-${currentPage}-${itemsPerPage}`">
      <span class="pagination-info__text">
        <span v-if="totalPages > 1" class="pagination-info__page-badge">
          Page {{ normalizedCurrentPage }} of {{ totalPages }}
        </span>
        Showing {{ paginationStart }} to {{ paginationEnd }} of {{ totalItems }} {{ itemLabel }}
      </span>
    </div>

    <div v-if="totalPages > 1" class="pagination-controls">
      <button
        type="button"
        class="pagination-btn pagination-prev"
        aria-label="Previous page"
        @click="goToPreviousPage"
        :disabled="normalizedCurrentPage === 1"
      >
        <i class="bi bi-chevron-left" aria-hidden="true"></i>
        <span>Previous</span>
      </button>

      <div class="pagination-pages">
        <button
          v-for="page in visiblePages"
          :key="page"
          type="button"
          class="pagination-btn pagination-page"
          :class="{ 'pagination-page--active': isCurrentPage(page) }"
          :aria-label="isCurrentPage(page) ? `Current page, page ${page}` : `Go to page ${page}`"
          :aria-current="isCurrentPage(page) ? 'page' : undefined"
          @click="goToPage(page)"
        >
          {{ page }}
        </button>
      </div>

      <button
        type="button"
        class="pagination-btn pagination-next"
        aria-label="Next page"
        @click="goToNextPage"
        :disabled="normalizedCurrentPage === totalPages"
      >
        <span>Next</span>
        <i class="bi bi-chevron-right" aria-hidden="true"></i>
      </button>
    </div>

    <div v-if="showItemsPerPage" class="pagination-size">
      <LabelDropdown
        label="Per page"
        inline
        :searchable="false"
        :select-id="itemsPerPageSelectId"
        v-model="itemsPerPageModel"
        :options="itemsPerPageOptions"
      />
    </div>
  </div>
</template>

<script>
import LabelDropdown from '../modules/consultant/components/LabelDropdown.vue';

export default {
  name: 'PaginationComponent',
  components: { LabelDropdown },
  props: {
    currentPage: {
      type: Number,
      required: true,
    },
    totalItems: {
      type: Number,
      required: true,
    },
    itemsPerPage: {
      type: Number,
      default: 10,
    },
    itemLabel: {
      type: String,
      default: 'items',
    },
    showItemsPerPage: {
      type: Boolean,
      default: true,
    },
    maxVisiblePages: {
      type: Number,
      default: 5,
    },
    perPageOptions: {
      type: Array,
      default: null,
    },
    selectId: {
      type: String,
      default: 'pagination-items-per-page',
    },
  },
  emits: ['page-change', 'items-per-page-change'],
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    normalizedCurrentPage() {
      const page = Number(this.currentPage);
      if (!Number.isFinite(page) || page < 1) return 1;
      return Math.min(Math.floor(page), this.totalPages || 1);
    },
    paginationStart() {
      return this.totalItems === 0 ? 0 : (this.normalizedCurrentPage - 1) * this.itemsPerPage + 1;
    },
    paginationEnd() {
      const end = this.normalizedCurrentPage * this.itemsPerPage;
      return Math.min(end, this.totalItems);
    },
    visiblePages() {
      const pages = [];
      let start = Math.max(1, this.normalizedCurrentPage - Math.floor(this.maxVisiblePages / 2));
      let end = Math.min(this.totalPages, start + this.maxVisiblePages - 1);

      if (end - start + 1 < this.maxVisiblePages) {
        start = Math.max(1, end - this.maxVisiblePages + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },
    itemsPerPageSelectId() {
      return this.selectId;
    },
    itemsPerPageOptions() {
      const sizes = this.perPageOptions || [5, 10, 20, 50, 100];
      return sizes.map((size) => ({
        value: String(size),
        label: String(size),
      }));
    },
    itemsPerPageModel: {
      get() {
        return String(this.itemsPerPage);
      },
      set(value) {
        const parsed = parseInt(value, 10);
        if (!Number.isNaN(parsed) && parsed !== this.itemsPerPage) {
          this.$emit('items-per-page-change', parsed);
        }
      },
    },
  },
  methods: {
    isCurrentPage(page) {
      return Number(page) === this.normalizedCurrentPage;
    },
    goToPage(page) {
      const target = Number(page);
      if (
        Number.isFinite(target) &&
        target >= 1 &&
        target <= this.totalPages &&
        target !== this.normalizedCurrentPage
      ) {
        this.$emit('page-change', target);
      }
    },
    goToPreviousPage() {
      if (this.normalizedCurrentPage > 1) {
        this.$emit('page-change', this.normalizedCurrentPage - 1);
      }
    },
    goToNextPage() {
      if (this.normalizedCurrentPage < this.totalPages) {
        this.$emit('page-change', this.normalizedCurrentPage + 1);
      }
    },
  },
};
</script>

<style src="./PaginationComponent.css"></style>
